/**
 * The hover and click surfaces behind a policy chip in the composer.
 *
 * Hover gives a preview card: what the document is, where it applies, and how
 * far ingestion got — the facts that decide whether this is the document you
 * meant. It deliberately stays four lines; a policy has a dozen fields and
 * showing them here would make a hover feel like a page.
 *
 * Click opens the document itself in a dialog, so checking a policy never
 * costs you the prompt you were writing.
 */
import { useMemo, useState, type ReactNode } from "react";
import { AlertCircleIcon, CheckCircle2Icon, LoaderCircleIcon } from "lucide-react";

import { resolvePrimaryEnvironmentHttpUrl } from "../../environments/primary/target";
import { useActiveOrg } from "../../state/orgs";
import { usePolicies, policyDisplayName } from "../../state/policies";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { HoverCard, HoverCardPopup, HoverCardTrigger } from "../ui/hover-card";

/** Ingestion state, in the same shapes the Policies page uses. */
function PolicyStatus({ status }: { readonly status: string }) {
  if (status === "ready") {
    return (
      <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
        <CheckCircle2Icon className="size-3" />
        Ready
      </span>
    );
  }
  if (status === "processing") {
    return (
      <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
        <LoaderCircleIcon className="size-3 animate-spin" />
        Processing
      </span>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-1 text-xs text-destructive">
      <AlertCircleIcon className="size-3" />
      Failed
    </span>
  );
}

const formatUploaded = (value: string): string => {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return "";
  }
  return new Date(parsed).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * The dot-separated facts under the title. Absent numbers are dropped rather
 * than shown as "—": a preview should not spend a line telling you it has
 * nothing to say.
 */
const summaryParts = (policy: {
  readonly pageCount: number | null;
  readonly createdAt: string;
}): ReadonlyArray<string> => {
  const parts: Array<string> = [];
  if (policy.pageCount !== null && policy.pageCount > 0) {
    parts.push(`${policy.pageCount} ${policy.pageCount === 1 ? "page" : "pages"}`);
  }
  const uploaded = formatUploaded(policy.createdAt);
  if (uploaded) {
    parts.push(`Added ${uploaded}`);
  }
  return parts;
};

export function PolicyMentionCard({
  policyId,
  label,
  children,
}: {
  readonly policyId: string;
  readonly label: string;
  /** The chip itself — this component only adds hover and click behaviour. */
  readonly children: ReactNode;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const activeOrg = useActiveOrg();
  const organizationId = activeOrg?.id ?? null;
  const { snapshot } = usePolicies(organizationId);

  const policy = snapshot?.policies.find((entry) => entry.id === policyId) ?? null;
  const jurisdictionNames = useMemo(
    () => new Map((snapshot?.jurisdictions ?? []).map((item) => [item.slug, item.name])),
    [snapshot],
  );
  const jurisdictions = (policy?.jurisdictionSlugs ?? []).map(
    (slug) => jurisdictionNames.get(slug) ?? slug,
  );
  const documentUrl =
    organizationId === null
      ? null
      : resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/download", {
          organizationId,
          policyId,
        });
  const parts = policy === null ? [] : summaryParts(policy);

  return (
    <>
      <HoverCard>
        <HoverCardTrigger
          render={
            <span
              role="button"
              tabIndex={0}
              onClick={() => setDialogOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setDialogOpen(true);
                }
              }}
              className="cursor-pointer"
            />
          }
        >
          {children}
        </HoverCardTrigger>
        <HoverCardPopup className="w-72 p-3">
          <p className="truncate text-sm font-medium text-foreground">{policy ? policyDisplayName(policy) : label}</p>
          {jurisdictions.length > 0 ? (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {jurisdictions.join(", ")}
            </p>
          ) : null}
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            {policy ? <PolicyStatus status={policy.status} /> : null}
            {parts.length > 0 ? <span className="truncate">{parts.join(" · ")}</span> : null}
          </div>
          {/* The tooltip this card replaced said exactly this; it is the one
              thing a reader cannot infer from the fields above. */}
          <p className="mt-2 border-t border-border/60 pt-2 text-xs text-muted-foreground">
            Organization policy — the agent reads this document from the knowledge base. Click to
            open it.
          </p>
        </HoverCardPopup>
      </HoverCard>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogPopup className="flex h-[80vh] max-w-4xl flex-col">
          <DialogHeader>
            <DialogTitle className="truncate">{policy ? policyDisplayName(policy) : label}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {policy ? <PolicyStatus status={policy.status} /> : null}
              {jurisdictions.length > 0 ? <span>{jurisdictions.join(", ")}</span> : null}
              {parts.length > 0 ? <span>{parts.join(" · ")}</span> : null}
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="min-h-0 flex-1 p-0">
            {documentUrl === null ? (
              <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
                This document is not available in the current organization.
              </div>
            ) : (
              <iframe
                src={documentUrl}
                title={policy ? policyDisplayName(policy) : label}
                className="size-full rounded-b-[inherit] border-0 bg-muted/30"
              />
            )}
          </DialogPanel>
          <div className="flex justify-end gap-2 px-6 pb-4">
            {documentUrl !== null ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(documentUrl, "_blank", "noopener")}
              >
                Open in new tab
              </Button>
            ) : null}
            <DialogClose render={<Button size="sm">Done</Button>} />
          </div>
        </DialogPopup>
      </Dialog>
    </>
  );
}
